import { EIcons } from '@/components/atoms/at-icon/icons';
import { EThemes } from '@/lib/contentstack-sdk/types';

import { ETextVariants } from '@/components/atoms/at-text/at-text.types';
import {
  EClickableSize,
  EClickableTypes,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import RenderComponents from '@/components/helpers/cms/RenderComponents';
import { RenderRichText } from '@/components/helpers/cms/RenderRichText';

import {
  parseGlobalLinkFromCMS,
  parseIconFromCMS,
  parseImageFromCMS,
} from '@/lib/contentstack-sdk/utils/parseHelpers';
import { formatModelHomeUrl } from '@/components/helpers/formatters/model-homes';
import { EOrContentMediaBlockSize } from '@/components/organisms/or-content-media-block/or-content-media-block.types';
import {
  EDividerDirectionalVariants,
  EDividerColors,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';
import { OrMiniCtaBlockA } from '@/components/organisms/or-mini-cta-block-a/or-mini-cta-block-a';
import { OrFeaturedVideo } from '@/components/organisms/or-featured-video/or-featured-video';
import { OrFloorPlanViewer } from '@/components/organisms/or-floorplan-viewer/or-floorplan-viewer';
import { OrCarouselHero } from '@/components/organisms/or-carousel-hero/or-carousel-hero';
import { MlCopyBlock } from '@/components/molecules/ml-copy-block/ml-copy-block';
import { EMlHeaderSize } from '@/components/molecules/ml-header/ml-header.types';
import { MlHeader } from '@/components/molecules/ml-header/ml-header';
import { EMlCardsTypes } from '@/components/molecules/ml-card/ml-card.types';
import { MlCard } from '@/components/molecules/ml-card/ml-card';
import { OrContentMediaBlock } from '@/components/organisms/or-content-media-block/or-content-media-block';
import { OrFaqList } from '@/components/organisms/or-faq-list/or-faq-list';
import { OrFaqListCS } from '@/components/organisms/or-faq-list/or-faq-list.cms';
import { MlAccordionItem } from '@/components/molecules/ml-accordion/ml-accordion';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtMedia } from '@/components/atoms/at-media/at-media';
import { ITmModelHomeDetailsProps } from './tm-model-home-detail.types';
import { TmModelHomeDetailClient } from './tm-model-home-detail.client';
import { IAtMediaProps } from '@/components/atoms/at-media/at-media.types';
import { ISlide } from '@/components/organisms/or-carousel-hero/or-carousel-hero.types';

export const TmModelHomeDetail = ({
  block,
  studio,
  floorPlans,
  additionalModelData,
  additionalModelHomesLabel,
}: ITmModelHomeDetailsProps) => {
  const {
    hours,
    hero_gallery,
    phone_number,
    directions_link,
    featured_media,
    body,
    faq,
    awards_banner,
    tour_banner,
    model_details,
    book_appointment,
    model_home_name,
    model_home_address,
  } = block;

  const TextContent = (
    <>
      <AtHeader element="h1" alt className="pb-10">
        {model_home_name}
      </AtHeader>
      <AtText
        type={ETextVariants.LABEL}
        className="flex flex-row items-center gap-x-1 pb-3.25 text-18 font-semibold uppercase tracking-[4%] text-skin-tertiary"
      >
        <span className="text-24">
          <AtIcon
            icon={EIcons.LOCATION}
            className="fill-[var(--color-text-tertiary)]"
            color="var(--color-text-tertiar)"
          />
        </span>
        Model Home Location
      </AtText>
      <AtText>{model_home_address}</AtText>
      <AtText className="pb-6">
        {studio?.City}, {studio?.State} {studio?.Zip}
      </AtText>
      <div className="flex flex-row gap-6">
        <AtClickable
          type={EClickableTypes.LINK}
          href={`tel:${phone_number}`}
          size={EClickableSize.LG}
          interactLocation="tm-model-home-detail"
        >
          {phone_number}
        </AtClickable>
        <AtDivider
          customClasses="h-35"
          direction={EDividerDirectionalVariants.VERTICAL}
          color={EDividerColors.BLACK}
          intent={EDividerVariants.THICK}
        />
        <AtClickable
          type={EClickableTypes.LINK}
          href={directions_link}
          size={EClickableSize.LG}
          interactLocation="tm-model-home-detail"
        >
          Get Directions
        </AtClickable>
      </div>
      <div className="pt-7">
        <AtText
          type={ETextVariants.LABEL}
          className="flex flex-row items-center pb-3.5 text-18 font-semibold uppercase tracking-[4%] text-skin-tertiary"
        >
          Hours
        </AtText>
        <ul className="pb-10">
          {hours.map((date) => {
            const {
              days,
              hours,
              _metadata: { uid },
            } = date.dates;
            return (
              <li key={uid}>
                <AtText>
                  <span className="font-semibold">{days}: </span>
                  {hours}
                </AtText>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );

  const RenderFaqLeftContent = () => {
    return (
      <MlCopyBlock title="Model Home FAQs">
        <AtText>
          We&apos;re here to help you figure out everything you need to know to
          have a successful and stress-free homebuilding process.
        </AtText>
        <AtClickable
          variant={EClickableVariants.PRIMARY}
          href="/faq"
          interactLocation="tm-model-home-detail"
        >
          See All FAQs
        </AtClickable>
      </MlCopyBlock>
    );
  };

  const RenderBookAppointmentContent = () => {
    return (
      <AtMedia
        {...parseImageFromCMS(book_appointment?.image)!}
        sizes="(max-width: 1023px) 100vw, 50vw"
      />
    );
  };

  return (
    <div>
      <TmModelHomeDetailClient />
      <OrCarouselHero
        textContent={TextContent}
        slideData={hero_gallery
          .map(({ global_field, _metadata }) => ({
            id: _metadata.uid,
            image: parseImageFromCMS(global_field) as IAtMediaProps | undefined,
          }))
          .filter((slide): slide is ISlide => slide.image !== undefined)}
      />

      {awards_banner.show_banner && (
        <OrMiniCtaBlockA
          topSpacing="lg"
          bottomSpacing="lg"
          icon={parseIconFromCMS(awards_banner?.icon)}
          theme={EThemes[awards_banner?.theme?.theme]}
          content_title={awards_banner?.heading}
          title_variant={awards_banner?.heading_variant?.select || 'h2'}
          title_element={awards_banner?.heading_element?.select || 'h2'}
        >
          <RenderRichText body={awards_banner?.description} />
        </OrMiniCtaBlockA>
      )}
      <OrFloorPlanViewer
        heading={{
          ...model_details?.copy_block,
          heading_element: model_details?.copy_block?.heading_element,
          heading_variant: model_details?.copy_block?.heading_variant,
        }}
        bottomSpacing="lg"
        description={model_details?.copy_block?.description}
        blockAlignment={'LEFT'}
        theme={EThemes.LIGHT}
        blueprints={floorPlans}
        floorPlansPDF={null}
      />
      {featured_media?.video_url && (
        <OrFeaturedVideo
          url={featured_media?.video_url}
          playButtonAriaLabel={
            featured_media?.video_button_aria_label || 'Play Video'
          }
          thumbnail={parseImageFromCMS(featured_media.video_thumbnail)}
          title={featured_media?.title}
          description={<RenderRichText body={featured_media?.description} />}
        />
      )}
      {tour_banner.show_banner && (
        <OrMiniCtaBlockA
          topSpacing="lg"
          bottomSpacing="lg"
          icon={parseIconFromCMS(tour_banner?.icon)}
          theme={EThemes[tour_banner?.theme?.theme]}
          content_title={tour_banner?.heading}
          title_variant={tour_banner?.heading_variant?.select || 'h2'}
          title_element={tour_banner?.heading_element?.select || 'h2'}
          cta={parseGlobalLinkFromCMS(
            tour_banner?.banner_cta,
            'tm-model-home-detail',
          )}
        >
          <RenderRichText body={tour_banner?.description} />
        </OrMiniCtaBlockA>
      )}

      {additionalModelData?.length > 0 && studio && (
        <section className="container">
          <MlHeader
            className="pb-14"
            size={EMlHeaderSize.SMALL}
            headElement="h2"
            head={additionalModelHomesLabel}
            body={
              additionalModelData?.length === 1
                ? 'Tour the model home, conveniently located next to our Design Studio.'
                : 'Tour these model homes, conveniently located next to our Design Studio.'
            }
          />
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
            {additionalModelData?.map((home) => {
              const imageProps = {
                src: {
                  mobile:
                    home?.cms?.card_image?.mobile_image_url?.url ||
                    home.Image ||
                    '',
                  tablet:
                    home?.cms?.card_image?.mobile_image_url?.url ||
                    home.Image ||
                    '',
                  desktop:
                    home?.cms?.card_image?.mobile_image_url?.url ||
                    home.Image ||
                    '',
                },
                alt: '',
                priority: false,
                quality: 65,
              };

              // Removes the Exterior style from the default plan name
              const friendlyName = home?.Name?.replace(
                home?.ExteriorStyle?.Name,
                '',
              );
              return (
                <MlCard
                  key={home.objectID}
                  imageProps={imageProps}
                  variant={EMlCardsTypes.HOME_PLAN}
                  href={formatModelHomeUrl(
                    studio?.CsName || studio?.Name,
                    home?.Plan?.Name,
                    home?.ExteriorStyle?.Name,
                  )}
                  showFavouriteButton={false}
                  homePlanProps={{
                    id: home?.Plan?.Id || -1,
                    name: friendlyName || '',
                    description: home?.ExteriorStyle?.Name,
                    features: [
                      'Model Home',
                      `${home?.Plan?.Bedrooms} Bed`,
                      `${home?.Plan?.Bathrooms} Bath`,
                      `${home?.SquareFeet} SQ.Ft`,
                    ],
                    tags: [],
                  }}
                />
              );
            })}
          </div>
        </section>
      )}

      <OrContentMediaBlock
        RenderLeftContent={RenderBookAppointmentContent}
        theme={EThemes.LIGHT}
        size={EOrContentMediaBlockSize.LG}
        topSpacing="lg"
        bottomSpacing="xl"
      >
        <MlCopyBlock
          title={book_appointment?.copy_block?.title}
          titleElement={book_appointment?.copy_block?.heading_element?.select}
          titleVariant={book_appointment?.copy_block?.heading_variant?.select}
        >
          <RenderRichText body={book_appointment?.copy_block?.description} />
          <AtClickable
            label={book_appointment?.copy_block?.link?.url?.title}
            outline={book_appointment?.copy_block?.link?.outline}
            href={book_appointment?.copy_block?.link?.url?.href}
            interactLocation="tm-model-home-detail"
            variant={
              EClickableVariants[book_appointment?.copy_block?.link?.variant]
            }
          />
        </MlCopyBlock>
      </OrContentMediaBlock>

      {body && body.length > 0 && (
        <RenderComponents isPage={false} body={body} />
      )}

      {faq[0]?._content_type_uid === 'accordion' && (
        <OrFaqList
          RenderLeftContent={RenderFaqLeftContent}
          theme={EThemes.LIGHT}
          topSpacing="lg"
          bottomSpacing="lg"
          topDivider
        >
          {faq?.[0]?.items?.length > 0 ? (
            <>
              {faq?.[0]?.items?.map((props, index) => {
                const { title, body } = props.accordion_item;
                return (
                  <MlAccordionItem key={index} index={index} title={title}>
                    <RenderRichText body={body} />
                  </MlAccordionItem>
                );
              })}
            </>
          ) : (
            <AtText className="text-skin-error">
              Please add an accordion instance
            </AtText>
          )}
        </OrFaqList>
      )}

      {faq[0]?._content_type_uid === 'faq_list' && (
        <OrFaqListCS block={faq[0]} />
      )}
    </div>
  );
};
